import { IdentityCheckPublicService } from '../services/identityCheckPublicService';
import { IdentityCheckBaseStore } from '@roc/feature-borrowers';
import { GlobalStore } from '@roc/feature-app-core';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { flow, makeObservable, observable } from 'mobx';

export enum IdVerificationLinkStatus {
  VALID = 'VALID',
  NEW_LINK_SENT = 'NEW_LINK_SENT',
  INVALID = 'INVALID',
}

export class IdentityCheckPublicStore extends IdentityCheckBaseStore {

  private publicService = new IdentityCheckPublicService();

  token: string = null;
  linkStatus: IdVerificationLinkStatus | null = null;

  constructor(globalStore: GlobalStore) {
    super(globalStore);
    makeObservable(this, {
      token: observable,
      linkStatus: observable,
      loadFromLink: flow,
      uploadDriversLicenseFromLink: flow,
      saveFinancialInformationFromLink: flow,
    });
  }

  *loadFromLink(token: string) {
    this.token = token;
    this.linkStatus = null;
    if (!token) {
      this.linkStatus = IdVerificationLinkStatus.INVALID;
      return;
    }
    try {
      const response = yield this.publicService.getFormInfo(token);
      this.applyResponse(response?.data?.data, true);
    } catch (e) {
      this.linkStatus = IdVerificationLinkStatus.INVALID;
      this.globalStore.notificationStore.showErrorNotification({ message: GENERIC_ERROR_MESSAGE });
    }
  }

  *uploadDriversLicenseFromLink() {
    try {
      const response = yield this.publicService.uploadDriversLicense(
        this.token,
        this.getIdInformation(),
        this.driversLicense
      );
      return this.applyResponse(response?.data?.data, false);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({ message: GENERIC_ERROR_MESSAGE });
      return false;
    }
  }

  *saveFinancialInformationFromLink() {
    try {
      const response = yield this.publicService.saveFinancialInformation(
        this.token,
        this.getFinancialInformation()
      );
      const saved = this.applyResponse(response?.data?.data, false);
      if (saved) {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Information saved successfully.',
        });
      }
      return saved;
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({ message: GENERIC_ERROR_MESSAGE });
      return false;
    }
  }

  private applyResponse(data, loadForms: boolean): boolean {
    const status = data?.linkStatus as IdVerificationLinkStatus;
    if (status !== IdVerificationLinkStatus.VALID) {
      this.linkStatus = status || IdVerificationLinkStatus.INVALID;
      return false;
    }
    this.linkStatus = IdVerificationLinkStatus.VALID;
    this.formInfo = data;
    if (loadForms) {
      this.idInformationForm.loadForm(data);
      this.financialInformationForm.loadForm(data);
      this.idAndFinancialInformationOptionsFormStore.loadForm(data);
      this.idInformationStatus = this.getIdInformationStatus();
      this.financialInformationStatus = this.getFinancialInformationStatus();
      this.setDriversLicense(
        this.formInfo?.driversLicensePath
          ? ({ name: this.getFileName() } as any)
          : null
      );
    }

    return true;
  }

  private getIdInformation() {
    const {
      idType,
      passportNumber,
      passportExpiration,
      driversLicenseState,
      driversLicenseNumber,
    } = this.idInformationForm.getFormValues();
    return {
      idType,
      passportNumber,
      passportExpiration,
      driversLicenseState,
      driversLicenseNumber,
    };
  }

  private getFinancialInformation() {
    const { assets, liabilities } = this.financialInformationForm.getFormValues();
    return {
      assets,
      liabilities
    };
  }
}
